/**
 * Transaction detail modal/drawer
 * WP-TRANSFER-1: Added transfer management actions
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { format, parseISO } from 'date-fns';
import type { TransactionWithSplits, MarkTransferResponse, SplitLoanPaymentResponse, ReassignTransferResponse } from '@/services/transactions';
import { transferService, type BatchMarkTransfersResponse } from '@/services/transactions';
import { formatCurrency } from '@/utils/format';
import { useUpdateTransaction, useDeleteTransaction, useCategoryById, useAccounts } from '@/hooks';
import { useSimilarTransactions } from '@/hooks/useSimilarTransactions';
import CategorySelect from './CategorySelect';
import SimilarTransactionsModal, { type ApplyResult } from './SimilarTransactionsModal';
import MarkTransferModal from './MarkTransferModal';
import SuggestSimilarTransfersModal from './SuggestSimilarTransfersModal';
import SplitLoanPaymentModal from './SplitLoanPaymentModal';
import TransferPairInfo from './TransferPairInfo';
import ReassignTransferModal from './ReassignTransferModal';
import { Button } from '@/components/ui';
import { toast } from '@/components/ui/Toaster';
import { cn } from '@/utils/cn';
import type { CategoryType } from '@/types';
import { ApiClientError } from '@/services/api';

export interface TransactionDetailProps {
  transaction: TransactionWithSplits | null;
  isOpen: boolean;
  onClose: () => void;
  onTransactionUpdated?: () => void;
}

/**
 * Delete confirmation modal for transactions (WP-BUG-35)
 * Pattern from ConnectionCard.tsx:350-391
 */
function DeleteTransactionConfirmation({
  transactionName,
  onConfirm,
  onCancel,
  isDeleting,
}: {
  transactionName: string;
  onConfirm: () => void;
  onCancel: () => void;
  isDeleting: boolean;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div
        className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-transaction-dialog-title"
      >
        <h3 id="delete-transaction-dialog-title" className="text-lg font-semibold text-gray-900">
          Delete Transaction
        </h3>
        <p className="mt-2 text-sm text-gray-600">
          Are you sure you want to delete{' '}
          <span className="font-medium">{transactionName}</span>?
        </p>
        <p className="mt-2 text-xs text-gray-500">
          This is a soft delete. The transaction can be recovered if needed.
        </p>
        <div className="mt-6 flex justify-end gap-3">
          <Button variant="outline" onClick={onCancel} disabled={isDeleting}>
            Cancel
          </Button>
          <Button variant="danger" onClick={onConfirm} isLoading={isDeleting}>
            {isDeleting ? 'Deleting...' : 'Delete Transaction'}
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Transaction detail view with editing capabilities
 */
export default function TransactionDetail({
  transaction,
  isOpen,
  onClose,
  onTransactionUpdated,
}: TransactionDetailProps) {
  const [categoryId, setCategoryId] = useState<string | undefined>(undefined);
  const [notes, setNotes] = useState('');
  const [hasChanges, setHasChanges] = useState(false);

  // State for similar transactions modal
  const [showSimilarModal, setShowSimilarModal] = useState(false);
  const [pendingCategoryId, setPendingCategoryId] = useState<string | undefined>(undefined);

  // State for transfer modals (WP-TRANSFER-1)
  const [showMarkTransferModal, setShowMarkTransferModal] = useState(false);
  const [showSimilarTransfersModal, setShowSimilarTransfersModal] = useState(false);
  const [showSplitLoanModal, setShowSplitLoanModal] = useState(false);
  const [isUnmarkingTransfer, setIsUnmarkingTransfer] = useState(false);
  // WP-TRANSFER-4: Reassign transfer modal state
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [isUnlinkingTransfer, setIsUnlinkingTransfer] = useState(false);

  // State for delete confirmation (WP-BUG-35)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const { mutate: updateTransaction, isPending } = useUpdateTransaction();
  const deleteTransaction = useDeleteTransaction();
  const category = useCategoryById(transaction?.categoryId);
  const pendingCategory = useCategoryById(pendingCategoryId);
  const { data: accounts = [] } = useAccounts();

  // Fetch similar transactions when we have a pending category change
  // This is used to determine if we should show the similar transactions modal
  const { similarTransactions, isLoading: isLoadingSimilar } = useSimilarTransactions({
    transactionId: transaction?.id ?? '',
    enabled: !!pendingCategoryId && !!transaction?.id && !transaction?.categoryId,
  });

  // Get account for this transaction
  const account = accounts.find((acc) => acc.id === transaction?.accountId);

  // Check if transaction can be split as loan payment (WP-TRANSFER-1)
  // Transaction must be negative (expense) and have a loan account to transfer to
  const loanAccounts = useMemo(() => {
    return accounts.filter((acc) => acc.type === 'loan' && !acc.isHidden);
  }, [accounts]);

  const canSplitAsLoan = useMemo(() => {
    return (
      transaction &&
      transaction.amount < 0 &&
      !transaction.isTransfer &&
      loanAccounts.length > 0
    );
  }, [transaction, loanAccounts]);


  // Reset form when transaction changes
  useEffect(() => {
    if (transaction) {
      setCategoryId(transaction.categoryId);
      setNotes('');
      setHasChanges(false);
      setPendingCategoryId(undefined);
      setShowSimilarModal(false);
      setShowMarkTransferModal(false);
      setShowSimilarTransfersModal(false);
      setShowSplitLoanModal(false);
      setShowReassignModal(false);
      setShowDeleteConfirm(false);
    }
  }, [transaction]);

  // Track changes
  useEffect(() => {
    if (transaction) {
      const categoryChanged = categoryId !== transaction.categoryId;
      setHasChanges(categoryChanged || notes.length > 0);
    }
  }, [categoryId, notes, transaction]);

  // Perform the actual save operation
  const performSave = useCallback(() => {
    if (!transaction) return;

    updateTransaction(
      {
        id: transaction.id,
        payload: {
          categoryId,
          ...(notes.length > 0 && { notes }),
        },
      },
      {
        onSuccess: () => {
          onClose();
        },
      }
    );
  }, [transaction, categoryId, notes, updateTransaction, onClose]);

  const handleSave = useCallback(() => {
    if (!transaction || !hasChanges) return;

    // Check if this is a new category assignment (was uncategorized before)
    const isNewCategoryAssignment = !transaction.categoryId && categoryId;

    // If assigning a category for the first time, check for similar transactions
    if (isNewCategoryAssignment && !isLoadingSimilar && similarTransactions.length > 0) {
      // Store the pending category and show the modal
      setPendingCategoryId(categoryId);
      setShowSimilarModal(true);
      return;
    }

    // Otherwise, save directly
    performSave();
  }, [transaction, hasChanges, categoryId, isLoadingSimilar, similarTransactions.length, performSave]);

  // Handle similar transactions modal close (skip)
  const handleSimilarModalClose = useCallback(() => {
    setShowSimilarModal(false);
    // Still save the original transaction
    performSave();
  }, [performSave]);

  // Handle similar transactions modal apply
  const handleSimilarApply = useCallback((result: ApplyResult) => {
    setShowSimilarModal(false);
    // Show success toast with results
    const message = result.ruleCreated
      ? `Applied category to ${result.appliedCount} transactions and created a rule for future transactions.`
      : `Applied category to ${result.appliedCount} transactions.`;
    toast.success(message);
    // Save the original transaction
    performSave();
  }, [performSave]);

  // WP-TRANSFER-1: Handle mark as transfer success
  const handleMarkTransferSuccess = useCallback((response: MarkTransferResponse) => {
    setShowMarkTransferModal(false);
    toast.success('Transaction marked as transfer.');

    // If there are similar transactions, prompt to mark them too
    if (response.similarCount > 0) {
      setShowSimilarTransfersModal(true);
    } else {
      onClose();
      onTransactionUpdated?.();
    }
  }, [onClose, onTransactionUpdated]);

  // WP-TRANSFER-1: Handle unmark transfer
  const handleUnmarkTransfer = useCallback(async () => {
    if (!transaction) return;

    setIsUnmarkingTransfer(true);
    try {
      await transferService.unmarkTransfer(transaction.id);
      toast.success('Transfer marking removed.');
      onClose();
      onTransactionUpdated?.();
    } catch (err) {
      if (err instanceof ApiClientError) {
        toast.error(err.detail);
      } else if (err instanceof Error) {
        toast.error(err.message);
      } else {
        toast.error('Failed to unmark transfer');
      }
    } finally {
      setIsUnmarkingTransfer(false);
    }
  }, [transaction, onClose, onTransactionUpdated]);

  // WP-TRANSFER-4: Handle unlink transfer pair (keeps is_transfer=true)
  const handleUnlinkTransfer = useCallback(async () => {
    if (!transaction) return;

    setIsUnlinkingTransfer(true);
    try {
      await transferService.unlinkTransfer(transaction.id);
      toast.success('Transfer pair unlinked.');
      onClose();
      onTransactionUpdated?.();
    } catch (err) {
      if (err instanceof ApiClientError) {
        toast.error(err.detail);
      } else if (err instanceof Error) {
        toast.error(err.message);
      } else {
        toast.error('Failed to unlink transfer pair');
      }
    } finally {
      setIsUnlinkingTransfer(false);
    }
  }, [transaction, onClose, onTransactionUpdated]);

  // WP-TRANSFER-4: Handle reassign transfer success
  const handleReassignSuccess = useCallback((_response: ReassignTransferResponse) => {
    setShowReassignModal(false);
    toast.success('Transfer pair reassigned successfully.');
    onClose();
    onTransactionUpdated?.();
  }, [onClose, onTransactionUpdated]);

  // WP-TRANSFER-4: Handle view paired transaction
  const handleViewPairedTransaction = useCallback((_txnId: string) => {
    // TODO: In a full implementation this would navigate to the paired transaction.
    // This would require routing integration or passing a callback from the parent.
    // For MVP, we show a toast indicating the feature is not yet implemented.
    toast.info('View paired transaction - feature coming soon');
  }, []);

  // WP-BUG-35: Handle delete transaction
  const handleDeleteTransaction = useCallback(async () => {
    if (!transaction) return;

    try {
      await deleteTransaction.mutateAsync(transaction.id);
      toast.success('Transaction deleted');
      setShowDeleteConfirm(false);
      onClose();
      onTransactionUpdated?.();
    } catch (err) {
      if (err instanceof ApiClientError && err.status === 404) {
        toast.error('Transaction not found or already deleted');
      } else if (err instanceof Error) {
        toast.error('Failed to delete transaction. Please try again.');
      } else {
        toast.error('Failed to delete transaction. Please try again.');
      }
      // Keep modal open for retry
    }
  }, [transaction, deleteTransaction, onClose, onTransactionUpdated]);

  // WP-TRANSFER-1: Handle similar transfers batch success
  const handleSimilarTransfersSuccess = useCallback((response: BatchMarkTransfersResponse) => {
    setShowSimilarTransfersModal(false);
    toast.success(`Marked ${response.successCount} additional transactions as transfers.`);
    onClose();
    onTransactionUpdated?.();
  }, [onClose, onTransactionUpdated]);

  // WP-TRANSFER-1: Handle similar transfers modal close (skip)
  const handleSimilarTransfersClose = useCallback(() => {
    setShowSimilarTransfersModal(false);
    onClose();
    onTransactionUpdated?.();
  }, [onClose, onTransactionUpdated]);

  // WP-TRANSFER-1: Handle split loan payment success
  const handleSplitLoanSuccess = useCallback((_response: SplitLoanPaymentResponse) => {
    setShowSplitLoanModal(false);
    toast.success('Loan payment split into expenses and principal.');
    onClose();
    onTransactionUpdated?.();
  }, [onClose, onTransactionUpdated]);

  const handleClose = () => {
    if (hasChanges) {
      // Could add confirmation dialog here
    }
    onClose();
  };

  if (!isOpen || !transaction) {
    return null;
  }

  const displayName = transaction.merchantName || transaction.name || 'Unknown';

  // Safely format date with fallback
  let formattedDate = 'Unknown date';
  try {
    if (transaction.date) {
      formattedDate = format(parseISO(transaction.date), 'EEEE, MMMM d, yyyy');
    }
  } catch {
    // Keep default 'Unknown date' if parsing fails
  }

  const isIncome = transaction.type === 'income';
  // WP-TRANSFER-5: Use the isTransfer boolean flag, not type field
  // type is based on amount direction (income/expense), isTransfer is the transfer marking
  const isTransfer = transaction.isTransfer;

  /**
   * Determine category type from transaction for filtering the category selector.
   * - Transfers show only transfer categories
   * - Positive amounts (income) show only income categories
   * - Negative amounts (expense) show only expense categories
   * - Zero amounts show all categories (rare, usually corrections)
   */
  const getCategoryType = (): CategoryType | undefined => {
    if (isTransfer) return 'transfer';
    if (transaction.amount > 0) return 'income';
    if (transaction.amount < 0) return 'expense';
    return undefined; // Zero amount - show all categories
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity"
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Drawer */}
      <div
        className={cn(
          'fixed inset-y-0 right-0 w-full max-w-md bg-white shadow-xl z-50',
          'transform transition-transform duration-300 ease-in-out',
          isOpen ? 'translate-x-0' : 'translate-x-full'
        )}
        role="dialog"
        aria-modal="true"
        aria-labelledby="transaction-detail-title"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2
            id="transaction-detail-title"
            className="text-lg font-semibold text-gray-900"
          >
            Transaction Details
          </h2>
          <button
            type="button"
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500 rounded"
            aria-label="Close"
          >
            <svg
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4 overflow-y-auto h-[calc(100%-140px)]">
          {/* Amount */}
          <div className="text-center mb-6">
            <p
              className={cn(
                'text-3xl font-bold',
                isIncome ? 'text-green-600' : isTransfer ? 'text-blue-600' : 'text-gray-900'
              )}
            >
              {isIncome ? '+' : isTransfer ? '' : '-'}
              {formatCurrency(Math.abs(transaction.amount), transaction.currency)}
            </p>
            <p className="text-sm text-gray-500 mt-1">{formattedDate}</p>
          </div>

          {/* Merchant/Description */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Merchant / Description
            </label>
            <p className="text-gray-900">{displayName}</p>
            {transaction.merchantName && transaction.name !== transaction.merchantName && (
              <p className="text-sm text-gray-500 mt-1">{transaction.name}</p>
            )}
          </div>

          {/* Account */}
          {account && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Account
              </label>
              <p className="text-gray-900">
                {account.name}
                {account.mask && (
                  <span className="text-gray-500 ml-1">
                    (***{account.mask})
                  </span>
                )}
              </p>
            </div>
          )}

          {/* Type */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Type
            </label>
            <span
              className={cn(
                'inline-flex px-2.5 py-0.5 text-sm font-medium rounded-full',
                isIncome
                  ? 'bg-green-100 text-green-800'
                  : isTransfer
                  ? 'bg-blue-100 text-blue-800'
                  : 'bg-gray-100 text-gray-800'
              )}
            >
              {transaction.type.charAt(0).toUpperCase() + transaction.type.slice(1)}
            </span>
            {transaction.pending && (
              <span className="ml-2 inline-flex px-2.5 py-0.5 text-sm font-medium rounded-full bg-yellow-100 text-yellow-800">
                Pending
              </span>
            )}
          </div>

          {/* Category - Editable */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Category
            </label>
            <CategorySelect
              value={categoryId}
              onChange={(value) => setCategoryId(value)}
              placeholder="Uncategorized"
              categoryType={getCategoryType()}
            />
            {category && categoryId === transaction.categoryId && (
              <p className="text-xs text-gray-500 mt-1">
                Path: {category.path}
              </p>
            )}
          </div>

          {/* Notes - Editable */}
          <div className="mb-6">
            <label
              htmlFor="transaction-notes"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Notes (optional)
            </label>
            <textarea
              id="transaction-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
              placeholder="Add a note..."
            />
          </div>

          {/* WP-TRANSFER-4: Transfer Pair Info (only for transfers) */}
          {isTransfer && (
            <TransferPairInfo
              transaction={transaction}
              accounts={accounts}
              onReassign={() => setShowReassignModal(true)}
              onUnlink={handleUnlinkTransfer}
              onViewPairedTransaction={handleViewPairedTransaction}
            />
          )}

          {/* Transfer Actions (WP-TRANSFER-1) */}
          <div className="mb-6 pt-4 border-t border-gray-200">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Transfer Actions
            </label>
            <div className="space-y-2">
              {/* Mark as Transfer / Unmark Transfer */}
              {isTransfer ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleUnmarkTransfer}
                  isLoading={isUnmarkingTransfer || isUnlinkingTransfer}
                  className="w-full justify-start"
                  leftIcon={
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  }
                >
                  Unmark Transfer
                </Button>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowMarkTransferModal(true)}
                  className="w-full justify-start"
                  leftIcon={
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                    </svg>
                  }
                >
                  Mark as Transfer
                </Button>
              )}

              {/* Split Loan Payment */}
              {canSplitAsLoan && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowSplitLoanModal(true)}
                  className="w-full justify-start"
                  leftIcon={
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                  }
                >
                  Split Loan Payment
                </Button>
              )}
            </div>
          </div>

          {/* Danger Zone (WP-BUG-35) */}
          <div className="mb-6 pt-4 border-t border-gray-200">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Danger Zone
            </label>
            <Button
              variant="danger"
              size="sm"
              onClick={() => setShowDeleteConfirm(true)}
              className="w-full justify-start"
              leftIcon={
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              }
            >
              Delete Transaction
            </Button>
          </div>

          {/* Transaction ID */}
          <div className="pt-4 border-t border-gray-200">
            <p className="text-xs text-gray-400">
              Transaction ID: {transaction.id}
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="absolute bottom-0 left-0 right-0 px-6 py-4 bg-white border-t border-gray-200">
          <div className="flex space-x-3">
            <Button variant="outline" onClick={handleClose} className="flex-1">
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={!hasChanges || isPending || isLoadingSimilar}
              isLoading={isPending || isLoadingSimilar}
              className="flex-1"
            >
              Save Changes
            </Button>
          </div>
        </div>
      </div>

      {/* Similar Transactions Modal */}
      {showSimilarModal && pendingCategoryId && pendingCategory && (
        <SimilarTransactionsModal
          isOpen={showSimilarModal}
          onClose={handleSimilarModalClose}
          originalTransaction={{
            id: transaction.id,
            name: transaction.merchantName || transaction.name || 'Unknown',
            amount: transaction.amount,
          }}
          newCategoryId={pendingCategoryId}
          newCategoryName={pendingCategory.name}
          onApply={handleSimilarApply}
        />
      )}

      {/* Mark Transfer Modal (WP-TRANSFER-1) */}
      {showMarkTransferModal && (
        <MarkTransferModal
          transaction={transaction}
          accounts={accounts}
          isOpen={showMarkTransferModal}
          onClose={() => setShowMarkTransferModal(false)}
          onSuccess={handleMarkTransferSuccess}
        />
      )}

      {/* Similar Transfers Modal (WP-TRANSFER-1) */}
      {showSimilarTransfersModal && (
        <SuggestSimilarTransfersModal
          transaction={transaction}
          isOpen={showSimilarTransfersModal}
          onClose={handleSimilarTransfersClose}
          onSuccess={handleSimilarTransfersSuccess}
        />
      )}

      {/* Split Loan Payment Modal (WP-TRANSFER-1) */}
      {showSplitLoanModal && loanAccounts.length > 0 && (
        <SplitLoanPaymentModal
          transaction={transaction}
          loanAccounts={loanAccounts}
          isOpen={showSplitLoanModal}
          onClose={() => setShowSplitLoanModal(false)}
          onSuccess={handleSplitLoanSuccess}
        />
      )}

      {/* Reassign Transfer Modal (WP-TRANSFER-4) */}
      {showReassignModal && (
        <ReassignTransferModal
          transaction={transaction}
          accounts={accounts}
          isOpen={showReassignModal}
          onClose={() => setShowReassignModal(false)}
          onSuccess={handleReassignSuccess}
        />
      )}

      {/* Delete Confirmation Modal (WP-BUG-35) */}
      {showDeleteConfirm && (
        <DeleteTransactionConfirmation
          transactionName={displayName}
          onConfirm={handleDeleteTransaction}
          onCancel={() => setShowDeleteConfirm(false)}
          isDeleting={deleteTransaction.isPending}
        />
      )}
    </>
  );
}
